/**
 * PortalScoringRules — Admin page for managing V2 intent + monetization scoring rules
 *
 * CRUD interface for the scoring_rules table. Rules are config-driven:
 * change weights, conditions, and active status without redeploying.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Brain, Target, Plus, Trash2, Save, Loader2, ToggleLeft, ToggleRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabaseGet, supabasePost, supabasePatch, supabaseDelete } from '@/utils/supabaseRest';

interface ScoringRule {
  id: string;
  score_type: 'intent' | 'monetization';
  rule_name: string;
  condition: { field: string; operator: string; value: unknown };
  points: number;
  vertical_id: string | null;
  is_active: boolean;
  priority: number;
}

export default function PortalScoringRules() {
  const { user } = useAuth();
  const [rules, setRules] = useState<ScoringRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'intent' | 'monetization'>('intent');

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const data = await supabaseGet('scoring_rules?order=priority.asc');
      setRules(data || []);
    } catch (err) {
      console.error('Failed to load scoring rules:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRules(); }, [loadRules]);

  const toggleRule = async (rule: ScoringRule) => {
    setSaving(rule.id);
    try {
      await supabasePatch(`scoring_rules?id=eq.${rule.id}`, { is_active: !rule.is_active });
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_active: !r.is_active } : r));
    } catch (err) {
      console.error('Failed to toggle rule:', err);
    } finally {
      setSaving(null);
    }
  };

  const updatePoints = async (rule: ScoringRule, newPoints: number) => {
    setSaving(rule.id);
    try {
      await supabasePatch(`scoring_rules?id=eq.${rule.id}`, { points: newPoints });
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, points: newPoints } : r));
    } catch (err) {
      console.error('Failed to update points:', err);
    } finally {
      setSaving(null);
    }
  };

  const deleteRule = async (ruleId: string) => {
    setSaving(ruleId);
    try {
      await supabaseDelete(`scoring_rules?id=eq.${ruleId}`);
      setRules(prev => prev.filter(r => r.id !== ruleId));
    } catch (err) {
      console.error('Failed to delete rule:', err);
    } finally {
      setSaving(null);
    }
  };

  const filteredRules = rules.filter(r => r.score_type === activeTab);

  const totalPositive = filteredRules.filter(r => r.is_active && r.points > 0).reduce((sum, r) => sum + r.points, 0);
  const totalNegative = filteredRules.filter(r => r.is_active && r.points < 0).reduce((sum, r) => sum + r.points, 0);

  return (
    <PortalLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Brain className="h-6 w-6 text-emerald-400" />
              Scoring Rules
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Configure how intent and monetization scores are calculated. All changes take effect immediately.
            </p>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('intent')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'intent'
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                : 'bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-600'
            }`}
          >
            <Target className="h-4 w-4 inline mr-1.5" />
            Intent Score ({filteredRules.length} rules)
          </button>
          <button
            onClick={() => setActiveTab('monetization')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'monetization'
                ? 'bg-violet-500/20 text-violet-400 border border-violet-500/40'
                : 'bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-600'
            }`}
          >
            <Brain className="h-4 w-4 inline mr-1.5" />
            Monetization Score ({rules.filter(r => r.score_type === 'monetization').length} rules)
          </button>
        </div>

        {/* Score summary */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-emerald-400">+{totalPositive}</div>
              <div className="text-xs text-slate-400">Max Positive</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-red-400">{totalNegative}</div>
              <div className="text-xs text-slate-400">Max Negative</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-white">{Math.min(100, Math.max(0, totalPositive + totalNegative))}</div>
              <div className="text-xs text-slate-400">Theoretical Max</div>
            </CardContent>
          </Card>
        </div>

        {/* Rules list */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 text-emerald-400 animate-spin" />
          </div>
        ) : (
          <div className="space-y-2">
            {filteredRules.map((rule) => (
              <Card key={rule.id} className={`border transition-colors ${
                rule.is_active ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-900/50 border-slate-800 opacity-50'
              }`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Toggle */}
                    <button onClick={() => toggleRule(rule)} className="shrink-0" disabled={saving === rule.id}>
                      {rule.is_active ? (
                        <ToggleRight className="h-6 w-6 text-emerald-400" />
                      ) : (
                        <ToggleLeft className="h-6 w-6 text-slate-600" />
                      )}
                    </button>

                    {/* Rule info */}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white">{rule.rule_name.replace(/_/g, ' ')}</div>
                      <div className="text-xs text-slate-500 font-mono mt-0.5">
                        {rule.condition.field} {rule.condition.operator} {JSON.stringify(rule.condition.value)}
                      </div>
                    </div>

                    {/* Points editor */}
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => updatePoints(rule, rule.points - 5)}
                        className="w-7 h-7 rounded bg-slate-700 text-slate-300 hover:bg-slate-600 flex items-center justify-center text-sm"
                      >-</button>
                      <span className={`w-12 text-center font-bold text-sm ${
                        rule.points > 0 ? 'text-emerald-400' : rule.points < 0 ? 'text-red-400' : 'text-slate-400'
                      }`}>
                        {rule.points > 0 ? '+' : ''}{rule.points}
                      </span>
                      <button
                        onClick={() => updatePoints(rule, rule.points + 5)}
                        className="w-7 h-7 rounded bg-slate-700 text-slate-300 hover:bg-slate-600 flex items-center justify-center text-sm"
                      >+</button>
                    </div>

                    {/* Delete */}
                    <button
                      onClick={() => deleteRule(rule.id)}
                      disabled={saving === rule.id}
                      className="shrink-0 text-slate-600 hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
